import React, { useMemo, useState, useEffect } from 'react';
import { useEntityRouter } from '../hooks/useEntityRouter';
import { Box, Button, IconButton, Stack } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import { useForm} from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import DataTable from '../components/DataTable';
import ConfirmDeleteDialog from '../components/ConfirmDeleteDialog';
import ChainFormDialog from '../components/ChainFormDialog';
import { getStoresByChainAndTown, TwoGISStoreResponse, listStores } from '../services/storeService';

import {
  listChains,
  createChain,
  updateChain,
  deleteChain,
  Chain,
} from '../services/chainService';

const schema = yup
  .object({
    name: yup.string().required('Name is required'),
    contact_info: yup.string().default(''),
    description: yup.string().default(''),
  })
  .required();

type FormValues = yup.InferType<typeof schema>;

const ChainsPage: React.FC = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { entityId, navigateToEntity, navigateToList } = useEntityRouter('/chains');
  // dialogs state
  const [openForm, setOpenForm] = useState(false);
  const [editData, setEditData] = useState<Chain | null>(null);
  const [deleteId, setDeleteId] = useState<number | null>(null);

  // react-hook-form
  const {
    reset,
    formState: { },
  } = useForm<FormValues>({
    resolver: yupResolver(schema),
    defaultValues: { name: '', contact_info: '', description: '' },
  });

  // Fetch companies
  const { data: chains = [], isLoading } = useQuery({
    queryKey: ['chains'],
    queryFn: listChains,
  });

  const { data: stores = [] } = useQuery({ queryKey: ['stores'], queryFn: () => listStores() });

  // Load specific chain if entityId is present
  useEffect(() => {
    if (entityId && chains.length > 0) {
      const chain = chains.find((c: Chain) => c.id === parseInt(entityId));
      if (chain) {
        handleEdit(chain);
      }
    }
  }, [entityId, chains]);

  // Mutations
  const createMutation = useMutation({
    mutationFn: (values: Omit<Chain, 'id'>) => createChain(values),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['chains'] }),
  });

  const updateMutation = useMutation({
    mutationFn: (input: Chain) => updateChain(input.id, input),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['chains'] }),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => deleteChain(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['chains'] }),
  });

  const onChainSubmit = (chain: Partial<Chain>) => {
    if (editData) {
      updateMutation.mutate({ ...editData, ...chain });
    } else {
      createMutation.mutate(chain as Omit<Chain, 'id'>);
    }
    setOpenForm(false);
    // Navigate back to list view after saving
    if (entityId) {
      navigateToList();
    }
  };

  const handleCloseForm = () => {
    setOpenForm(false);
    // Navigate back to list view when closing without saving
    if (entityId) {
      navigateToList();
    }
  };

  const handleAdd = () => {
    setEditData(null);
    reset({ name: '', contact_info: '', description: '' });
    setOpenForm(true);
  };

  const handleEdit = (row: Chain) => {
    setEditData(row);
    reset({ name: row.name, contact_info: row.contact_info || '', description: row.description || '' });
    setOpenForm(true);
  };

  const handleRowClick = (row: Chain) => {
    navigateToEntity(row.id);
  };

  const handleFetch2GISStores = async (chainName: string, town: string, addToDb: boolean = false): Promise<TwoGISStoreResponse> => {
    return await getStoresByChainAndTown(chainName, town, addToDb);
  };

  // Columns definition
  const columns = useMemo(
    () => [
      { field: 'name', headerName: t('chains.name'), flex: 1 },
      { field: 'contact_info', headerName: t('chains.contact'), flex: 1 },
      { field: 'description', headerName: t('chains.description'), flex: 1 },
      {
        field: 'actions',
        headerName: t('chains.actions'),
        width: 120,
        sortable: false,
        renderCell: (params: any) => (
          <Stack direction="row" spacing={1}>
            <IconButton size="small" onClick={() => handleEdit(params.row)}>
              <EditIcon />
            </IconButton>
            <IconButton size="small" color="error" onClick={() => setDeleteId(params.row.id)}>
              <DeleteIcon />
            </IconButton>
          </Stack>
        ),
      },
    ],
    []
  );

  return (
    <Box p={2}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
        <h2>{t('chains.title')}</h2>
        <Button variant="contained" startIcon={<AddIcon />} onClick={handleAdd}>
          {t('chains.add')}
        </Button>
      </Stack>

      <DataTable 
        rows={chains} 
        columns={columns} 
        loading={isLoading} 
        onRowDoubleClick={handleEdit}
        onRowClick={handleRowClick}
      />

      {/* Create/Edit Dialog */}
      <ChainFormDialog
        open={openForm}
        onClose={handleCloseForm}
        onSubmit={onChainSubmit}
        initialValues={editData || undefined}
        stores={stores}
        onFetch2GIS={handleFetch2GISStores}
      />

      {/* Delete Confirmation */}
      <ConfirmDeleteDialog
        open={deleteId !== null}
        onCancel={() => setDeleteId(null)}
        onConfirm={() => {
          if (deleteId) deleteMutation.mutate(deleteId);
          setDeleteId(null);
        }}
      />
    </Box>
  );
};

export default ChainsPage; 